import { put, select, takeEvery } from 'typed-redux-saga';

import logger from '@/domains/core/observability/logger';
import { TRACKING_DONE } from '@/domains/core/tracking/tracking.actions';
import { Gtm } from '@/domains/core/tracking/utils/Gtm';
import { eligibilityCouponSelector } from '@/domains/loyalty/modules/CouponEligibility/hooks/useEligibilityCoupon';
import type { EligibilityCoupon } from '@/domains/loyalty/modules/CouponEligibility/interfaces/EligibilityCoupon';
import type { MdScoresState } from '@/domains/productDiscovery/ArticleReviews/ArticleReviews.model';
import { mdScoresSelector } from '@/domains/productDiscovery/ArticleReviews/MdScores.ducks';
import { searchInputSelector } from '@/domains/productDiscovery/HeaderLeonidas/modules/searchBar/selectors/searchInput';
import { brandSelector } from '@/domains/productDiscovery/ProductPage/modules/Brand/brand.ducks';
import { type Brand } from '@/domains/productDiscovery/ProductPage/modules/Brand/brand.model';
import { detailSelector } from '@/domains/productDiscovery/ProductPage/selectors/detail';
import { GTM_VIEW_PRODUCT_PAGE } from '@/domains/productDiscovery/ProductPage/utils/gtm/gtmEvents';
import { selectSponsoredProductIds } from '@/domains/productDiscovery/SponsoredProductRecommendation/selectors/sponsoredProductRecommendation';
import { SPONSORED_PRODUCT_MIN_LENGTH } from '@/domains/productDiscovery/SponsoredProductRecommendation/services/constants';
import { BulkPurchaseData } from '@/productDiscovery/ProductPage/common/modules/BulkPurchase/interfaces/BulkPurchase.model';
import type { MabayaProduct } from '@/productDiscovery/SponsoredProducts/interfaces/MabayaProduct';
import { SPTrackingApi } from '@/productDiscovery/SponsoredProducts/services/tracking/api';

import { selectBulkPurchase } from './common/modules/BulkPurchase/ducks/BulkPurchase.selectors';
import type { MarketDetail } from './interfaces/Detail.model';

export const VIEW_PRODUCT_PAGE_TRACKING_DONE =
  'product/VIEW_PRODUCT_PAGE_TRACKING_DONE';

export function* sendProductTrackingSaga() {
  try {
    const detail = (yield* select(detailSelector)) as MarketDetail;

    const brand = (yield* select(brandSelector)) as Brand;

    const { data } = (yield* select(mdScoresSelector)) as MdScoresState;

    const sponsoredProductIds = (yield* select(selectSponsoredProductIds)) as
      | MabayaProduct[]
      | undefined;

    const eligibilityCoupon = (yield* select(eligibilityCouponSelector)) as
      | EligibilityCoupon
      | undefined;

    const coupons = eligibilityCoupon
      ? [
          {
            amount: eligibilityCoupon.amount.toString(),
            code: eligibilityCoupon.code,
          },
        ]
      : [];

    const bulkData = (yield select(selectBulkPurchase)) as BulkPurchaseData[];

    Gtm.pushEvent(
      GTM_VIEW_PRODUCT_PAGE({
        brand,
        detail,
        coupons,
        isSponsoredProductAvailable:
          (sponsoredProductIds?.length ?? 0) > SPONSORED_PRODUCT_MIN_LENGTH,
        isBulkAvailable: !!bulkData?.length,
        mdScores: data,
      }),
    );

    const signal = (yield* select(searchInputSelector)) as string | undefined;

    if (detail.productId && detail.seller && detail.seller.id && signal) {
      SPTrackingApi.trackVisit({
        product_id: detail.productId,
        seller_id: detail.seller.id,
        signal,
      });
    }

    yield* put({ type: VIEW_PRODUCT_PAGE_TRACKING_DONE });
  } catch (error) {
    logger.error(error);
  }
}

export function* trackingSaga() {
  yield takeEvery(TRACKING_DONE, sendProductTrackingSaga);
}
